import { Fragment, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { css, cx } from "styled-system/css";
import { RefreshCw } from "lucide-react";
import { ak } from "./admin-styles";
import { api } from "~/lib/api";
import {
  Banner,
  PageHead,
  Section,
  Status,
  Time,
  Toggle,
  type StatusKind,
} from "~/components/editorial";

interface GitHubOrganization {
  id: string;
  installationId: number;
  name: string;
  avatarUrl: string | null;
  syncStatus: "idle" | "syncing" | "error" | "pending";
  syncProgress: number;
  syncMessage: string | null;
  syncError: string | null;
  syncStartedAt: string | null;
  estimatedTimeRemaining: string | null;
  lastSyncAt: string | null;
  repoCount: number;
  syncHealth?: SyncHealth;
}

interface SyncHealth {
  queueAvailable: boolean;
  queued: number;
  active: number;
  failedJobs: number;
  nextScheduledAt: string | null;
  stalled: boolean;
  lastRunStatus: string | null;
  lastRunAt: string | null;
  lastRunFailedRepos: string[];
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
}

interface Repository {
  id: string;
  name: string;
  fullName: string;
  orgName: string;
  defaultBranch: string;
  deploysOnMerge: boolean;
  prCount: number;
}

interface CopilotOrgStatus {
  id: string;
  name: string;
  copilotEnabled: boolean;
  lastSyncAt: string | null;
  syncError: string | null;
  seatCount: number;
  metricsAvailable: boolean;
}

export function GitHubSettingsPage() {
  const [searchParams] = useSearchParams();
  const [copilotSyncing, setCopilotSyncing] = useState<string | null>(null);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualInstallationId, setManualInstallationId] = useState("");
  const [manualOrgName, setManualOrgName] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const queryClient = useQueryClient();
  const orgsQuery = useQuery({
    queryKey: ["github-organizations"],
    queryFn: async () =>
      (await api.get<{ data: GitHubOrganization[] }>("/github/organizations"))
        .data,
    staleTime: 0,
    // Poll every 2 seconds while any org is syncing
    refetchInterval: (query) =>
      query.state.data?.some(isSyncing) ? 2000 : false,
  });
  const organizations = orgsQuery.data ?? [];
  const loading = orgsQuery.isLoading;
  const fetchOrgs = () => orgsQuery.refetch();
  const setOrganizations = (
    update: (prev: GitHubOrganization[]) => GitHubOrganization[],
  ) =>
    queryClient.setQueryData<GitHubOrganization[]>(
      ["github-organizations"],
      (prev) => update(prev ?? []),
    );

  const reposQuery = useQuery({
    queryKey: ["github-repositories"],
    queryFn: async () =>
      (await api.get<{ data: Repository[] }>("/github/repositories")).data,
    staleTime: 0,
  });
  const repositories = reposQuery.data ?? [];
  const reposLoading = reposQuery.isLoading;
  const setRepositories = (update: (prev: Repository[]) => Repository[]) =>
    queryClient.setQueryData<Repository[]>(["github-repositories"], (prev) =>
      update(prev ?? []),
    );

  const copilotQuery = useQuery({
    queryKey: ["copilot-orgs"],
    queryFn: async () =>
      (await api.get<{ data: CopilotOrgStatus[] }>("/copilot/orgs")).data,
    staleTime: 0,
  });
  const copilotOrgs = copilotQuery.data ?? [];
  const fetchCopilotOrgs = () => copilotQuery.refetch();

  // Ticks while a sync runs so the elapsed time stays current
  const hasActiveSyncs = organizations.some(isSyncing);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasActiveSyncs) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasActiveSyncs]);

  const handleCopilotSync = async (orgId: string) => {
    setCopilotSyncing(orgId);
    try {
      await api.post("/copilot/sync", { orgId });
      // Refresh Copilot status after a short delay
      setTimeout(() => {
        fetchCopilotOrgs();
        setCopilotSyncing(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to trigger Copilot sync:", err);
      setCopilotSyncing(null);
    }
  };

  const toggleDeploysOnMerge = async (
    repoId: string,
    deploysOnMerge: boolean,
  ) => {
    try {
      await api.patch(`/github/repositories/${repoId}`, { deploysOnMerge });
      setRepositories((prev) =>
        prev.map((r) => (r.id === repoId ? { ...r, deploysOnMerge } : r)),
      );
    } catch (err) {
      console.error("Failed to update repository:", err);
    }
  };

  // Check for success/error from OAuth callback
  const [seenParams, setSeenParams] = useState<URLSearchParams | null>(null);
  if (searchParams !== seenParams) {
    setSeenParams(searchParams);
    const statusParam = searchParams.get("status");
    if (statusParam === "success") setStatus("success");
    else if (statusParam === "error") setStatus("error");
  }
  // Clear the OAuth success banner after showing
  useEffect(() => {
    if (searchParams.get("status") !== "success") return;
    const timeout = setTimeout(() => setStatus(null), 5000);
    return () => clearTimeout(timeout);
  }, [searchParams]);

  const handleConnectOrg = async () => {
    try {
      const response = await api.get<{ url: string }>("/auth/github/install");
      window.location.href = response.url;
    } catch (err) {
      console.error("Failed to get GitHub install URL:", err);
    }
  };

  const handleSync = async (orgId: string, full = false) => {
    try {
      const url = full
        ? `/github/organizations/${orgId}/sync?full=true`
        : `/github/organizations/${orgId}/sync`;
      await api.post(url);
      // Update local state
      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === orgId
            ? { ...org, syncStatus: "pending" as const, syncProgress: 0 }
            : org,
        ),
      );
    } catch (err) {
      console.error("Failed to trigger sync:", err);
    }
  };

  const handleDelete = async (orgId: string) => {
    if (
      !confirm(
        "Are you sure you want to disconnect this organization? All synced data will be deleted.",
      )
    ) {
      return;
    }
    try {
      await api.delete(`/github/organizations/${orgId}`);
      setOrganizations((prev) => prev.filter((org) => org.id !== orgId));
    } catch (err) {
      console.error("Failed to delete organization:", err);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualInstallationId || !manualOrgName) return;

    setManualSubmitting(true);
    try {
      await api.post("/github/organizations", {
        installationId: parseInt(manualInstallationId, 10),
        name: manualOrgName,
        avatarUrl: null,
      });
      setStatus("success");
      setManualInstallationId("");
      setManualOrgName("");
      setShowManualEntry(false);
      fetchOrgs();
    } catch (err) {
      console.error("Failed to add organization:", err);
      setStatus("error");
    } finally {
      setManualSubmitting(false);
    }
  };

  const formatDuration = (startedAt: string | null) => {
    if (!startedAt) return null;
    const elapsed = now - new Date(startedAt).getTime();
    const seconds = Math.floor(elapsed / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  const health = organizations.map(orgHealth);
  const failing = health.filter((h) => h.kind === "fail").length;
  const totalRepos = organizations.reduce((s, o) => s + o.repoCount, 0);
  const deployRepos = repositories.filter((r) => r.deploysOnMerge).length;

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · GitHub"
        title="GitHub"
        dek={
          loading ? (
            "Organizations, sync health and the repos that count as deploys."
          ) : (
            <GitHubDek
              orgs={organizations.length}
              repos={totalRepos}
              failing={failing}
            />
          )
        }
      />

      {status === "success" && (
        <Banner>
          GitHub organization connected. The first sync is running.
        </Banner>
      )}

      {status === "error" && (
        <Banner tone="caution">
          Could not connect the GitHub organization. Try again.
        </Banner>
      )}

      <Section
        kicker="I. Organizations"
        title="Connected organizations and sync health"
        actions={
          <button className={ak.btn} onClick={handleConnectOrg}>
            Connect organization
          </button>
        }
      >
        {loading ? (
          <div className={ak.loading}>Loading…</div>
        ) : organizations.length > 0 ? (
          <div className={ak.tableWrap}>
            <table className={cx(ak.table, styles.orgTable)}>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Status</th>
                  <th data-num>Repos</th>
                  <th>Last complete sync</th>
                  <th>Next run</th>
                  <th data-num>Queued</th>
                  <th data-num>Failed jobs</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {organizations.map((org, i) => {
                  const h = health[i] ?? orgHealth(org);
                  const copilotOrg = copilotOrgs.find(
                    (c) => c.name === org.name,
                  );
                  const busy =
                    org.syncStatus === "syncing" ||
                    org.syncStatus === "pending";
                  const sh = org.syncHealth;
                  const showFailure =
                    !!sh?.lastFailureAt &&
                    org.syncStatus !== "error" &&
                    (!org.lastSyncAt ||
                      new Date(sh.lastFailureAt) > new Date(org.lastSyncAt));
                  const showPartial =
                    sh?.lastRunStatus === "partial" &&
                    org.syncStatus !== "syncing";
                  const showDone =
                    org.syncStatus === "idle" &&
                    !!org.syncMessage &&
                    org.syncProgress === 100 &&
                    sh?.lastRunStatus !== "partial";
                  const hasDetail =
                    org.syncStatus === "syncing" ||
                    org.syncStatus === "pending" ||
                    (org.syncStatus === "error" && !!org.syncError) ||
                    !!sh?.stalled ||
                    showPartial ||
                    showFailure ||
                    showDone ||
                    !!copilotOrg;

                  return (
                    <Fragment key={org.id}>
                      <tr data-has-detail={hasDetail || undefined}>
                        <td>
                          <div className={styles.orgCell}>
                            {org.avatarUrl ? (
                              <img
                                src={org.avatarUrl}
                                alt=""
                                className={ak.avatar}
                              />
                            ) : (
                              <span className={styles.avatarBlank} />
                            )}
                            <div>
                              <div className={styles.orgName}>{org.name}</div>
                              <div className={cx(ak.meta, ak.mono)}>
                                installation {org.installationId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Status kind={h.kind}>{h.label}</Status>
                        </td>
                        <td data-num>{org.repoCount}</td>
                        <td className={ak.mono}>
                          {org.lastSyncAt ? (
                            <Time iso={org.lastSyncAt} />
                          ) : (
                            <span className={ak.muted}>never</span>
                          )}
                        </td>
                        <td className={ak.mono}>
                          {sh?.nextScheduledAt ? (
                            <Time iso={sh.nextScheduledAt} />
                          ) : (
                            <span className={ak.muted}>–</span>
                          )}
                        </td>
                        <td data-num>{sh ? sh.queued + sh.active : "–"}</td>
                        <td data-num>{sh ? sh.failedJobs : "–"}</td>
                        <td>
                          <div className={ak.actions}>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(org.id)}
                              disabled={busy}
                              title="Quick sync: recent changes"
                            >
                              <RefreshCw
                                size={12}
                                className={
                                  org.syncStatus === "syncing"
                                    ? ak.spinning
                                    : ""
                                }
                              />
                              Sync
                            </button>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(org.id, true)}
                              disabled={busy}
                              title="Full sync: last 180 days, adds to existing data"
                            >
                              Full
                            </button>
                            <button
                              className={cx(ak.btnDanger, ak.btnSm)}
                              onClick={() => handleDelete(org.id)}
                              disabled={org.syncStatus === "syncing"}
                              title="Disconnect"
                            >
                              Disconnect
                            </button>
                          </div>
                        </td>
                      </tr>
                      {hasDetail && (
                        <tr data-detail>
                          <td colSpan={8}>
                            <div className={styles.detail}>
                              {org.syncStatus === "syncing" && (
                                <div className={styles.progressBlock}>
                                  <div className={ak.progress}>
                                    <div
                                      style={{
                                        width: `${org.syncProgress}%`,
                                      }}
                                    />
                                  </div>
                                  <div>
                                    {org.syncMessage || "Syncing…"}{" "}
                                    <span className={ak.mono}>
                                      {org.syncProgress}%
                                      {org.syncStartedAt &&
                                        ` · ${formatDuration(org.syncStartedAt)} elapsed`}
                                      {org.estimatedTimeRemaining &&
                                        ` · ${org.estimatedTimeRemaining} left`}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {org.syncStatus === "pending" && (
                                <Status kind="wait">
                                  Sync queued, waiting for a worker
                                </Status>
                              )}

                              {sh?.stalled && (
                                <Status kind="fail">
                                  Stuck: no worker is running this sync. Start a
                                  new sync to recover.
                                </Status>
                              )}

                              {showPartial && sh && (
                                <Status kind="fail">
                                  Last run had errors in{" "}
                                  <span className={ak.mono}>
                                    {sh.lastRunFailedRepos.length}
                                  </span>{" "}
                                  repos (
                                  <span className={ak.mono}>
                                    {sh.lastRunFailedRepos
                                      .slice(0, 3)
                                      .join(", ")}
                                    {sh.lastRunFailedRepos.length > 3
                                      ? ", …"
                                      : ""}
                                  </span>
                                  ). The next sync reads them again.
                                </Status>
                              )}

                              {showFailure && sh?.lastFailureAt && (
                                <Status kind="fail">
                                  Failed <Time iso={sh.lastFailureAt} />
                                  {sh.lastFailureMessage
                                    ? `: ${sh.lastFailureMessage}`
                                    : ""}
                                </Status>
                              )}

                              {org.syncStatus === "error" && org.syncError && (
                                <Status kind="fail">{org.syncError}</Status>
                              )}

                              {showDone && (
                                <Status kind="ok">{org.syncMessage}</Status>
                              )}

                              {copilotOrg && (
                                <div className={styles.copilotLine}>
                                  <span className={styles.copilotLabel}>
                                    Copilot
                                  </span>
                                  {copilotOrg.copilotEnabled ? (
                                    <Status kind="ok">Active</Status>
                                  ) : (
                                    <Status kind="off">Not available</Status>
                                  )}
                                  {copilotOrg.copilotEnabled && (
                                    <>
                                      <span className={ak.meta}>
                                        <span className="num">
                                          {copilotOrg.seatCount}
                                        </span>{" "}
                                        seats
                                        {copilotOrg.lastSyncAt && (
                                          <>
                                            {" "}
                                            · synced{" "}
                                            <Time iso={copilotOrg.lastSyncAt} />
                                          </>
                                        )}
                                      </span>
                                      <button
                                        className={ak.btnText}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopilotSync(copilotOrg.id);
                                        }}
                                        disabled={
                                          copilotSyncing === copilotOrg.id
                                        }
                                      >
                                        {copilotSyncing === copilotOrg.id
                                          ? "Syncing…"
                                          : "Sync Copilot"}
                                      </button>
                                    </>
                                  )}
                                  {copilotOrg.syncError && (
                                    <Status kind="fail">
                                      {copilotOrg.syncError}
                                    </Status>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={ak.empty}>
            <p>No GitHub organizations connected.</p>
            <p>
              Connect one to start counting pull requests, reviews and commits.
            </p>
          </div>
        )}

        <p className={ak.note}>
          Status reads the latest run: ● synced, ◆ running, ◇ queued, ▲ failed
          or stuck. Queued counts waiting plus running jobs. A full sync reads
          PRs and commits updated in the last 180 days and adds to existing
          data; it never clears old records.
        </p>

        <div className={styles.manual}>
          <button
            className={ak.btnText}
            onClick={() => setShowManualEntry(!showManualEntry)}
          >
            {showManualEntry
              ? "Hide manual entry"
              : "Add an organization by installation ID instead"}
          </button>

          {showManualEntry && (
            <form onSubmit={handleManualSubmit} className={styles.manualForm}>
              <p className={ak.hint}>
                Use this if the install redirect fails. The installation ID is
                in your GitHub App settings under Installations, or in the URL
                after installing.
              </p>
              <div className={ak.formRow}>
                <div className={ak.field}>
                  <label className={ak.label} htmlFor="orgName">
                    Organization name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    placeholder="my-org"
                    value={manualOrgName}
                    onChange={(e) => setManualOrgName(e.target.value)}
                    className={cx(ak.input, ak.mono)}
                    required
                  />
                </div>
                <div className={ak.field}>
                  <label className={ak.label} htmlFor="installationId">
                    Installation ID
                  </label>
                  <input
                    id="installationId"
                    type="number"
                    placeholder="12345678"
                    value={manualInstallationId}
                    onChange={(e) => setManualInstallationId(e.target.value)}
                    className={cx(ak.input, ak.mono)}
                    required
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className={ak.btnSecondary}
                  disabled={
                    manualSubmitting || !manualInstallationId || !manualOrgName
                  }
                >
                  {manualSubmitting ? "Adding…" : "Add organization"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Section>

      {/* DORA Metrics Repository Configuration */}
      {repositories.length > 0 && (
        <Section
          kicker="II. DORA"
          title="Repos that deploy on merge"
          dek={
            <>
              <span className={ak.mono}>{deployRepos}</span> of{" "}
              <span className={ak.mono}>{repositories.length}</span> repos count
              a merge to the default branch as a production deploy.
            </>
          }
        >
          {reposLoading ? (
            <div className={ak.loading}>Loading repositories…</div>
          ) : (
            <div className={ak.tableWrap}>
              <table className={ak.table}>
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Default branch</th>
                    <th data-num>PRs</th>
                    <th data-num>Deploys on merge</th>
                  </tr>
                </thead>
                <tbody>
                  {repositories.map((repo) => (
                    <tr key={repo.id}>
                      <td className={ak.mono}>{repo.fullName}</td>
                      <td className={cx(ak.mono, ak.muted)}>
                        {repo.defaultBranch}
                      </td>
                      <td data-num>{repo.prCount}</td>
                      <td data-num>
                        <Toggle
                          checked={repo.deploysOnMerge}
                          onChange={(next) =>
                            toggleDeploysOnMerge(repo.id, next)
                          }
                          label={`${repo.fullName} deploys on merge`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={ak.note}>
            Only switched-on repos feed deployment frequency, lead time and the
            failure-rate proxy. Turn it on for repos that ship continuously.
          </p>
        </Section>
      )}

      <Section kicker="III. Setup" title="Connecting an organization">
        <ol className={ak.steps}>
          <li>Click Connect organization above.</li>
          <li>Pick the GitHub organization.</li>
          <li>Choose which repositories the app can read, or all of them.</li>
          <li>Click Install to finish.</li>
          <li>Aura starts syncing pull request data on its own.</li>
        </ol>
        <p className={ak.note}>
          <strong>Note:</strong> installing needs admin access to the
          organization. The app asks for read access to pull requests, issues
          and repository metadata only.
        </p>
      </Section>
    </div>
  );
}

function GitHubDek({
  orgs,
  repos,
  failing,
}: {
  orgs: number;
  repos: number;
  failing: number;
}) {
  if (orgs === 0) {
    return <>No organizations connected yet.</>;
  }
  return (
    <>
      <span className={ak.mono}>{orgs}</span>{" "}
      {orgs === 1 ? "organization" : "organizations"} connected, covering{" "}
      <span className={ak.mono}>{repos}</span>{" "}
      {repos === 1 ? "repository" : "repositories"}.{" "}
      {failing === 0 ? (
        "Every sync is healthy."
      ) : (
        <>
          <span className={ak.mono}>{failing}</span> of{" "}
          <span className={ak.mono}>{orgs}</span>{" "}
          {failing === 1 ? "needs" : "need"} attention.
        </>
      )}
    </>
  );
}

function isSyncing(org: GitHubOrganization) {
  return org.syncStatus === "syncing" || org.syncStatus === "pending";
}

/** One status per org, worst condition first. */
function orgHealth(org: GitHubOrganization): {
  kind: StatusKind;
  label: string;
} {
  const sh = org.syncHealth;
  if (org.syncStatus === "syncing") return { kind: "busy", label: "Syncing" };
  if (org.syncStatus === "pending") return { kind: "wait", label: "Queued" };
  if (org.syncStatus === "error") return { kind: "fail", label: "Failing" };
  if (sh?.stalled) return { kind: "fail", label: "Stuck" };
  if (
    sh?.lastFailureAt &&
    (!org.lastSyncAt || new Date(sh.lastFailureAt) > new Date(org.lastSyncAt))
  ) {
    return { kind: "fail", label: "Last run failed" };
  }
  if (sh?.lastRunStatus === "partial")
    return { kind: "fail", label: "Partial" };
  if (org.lastSyncAt) return { kind: "ok", label: "Synced" };
  return { kind: "off", label: "Never synced" };
}

const styles = {
  orgTable: css({
    "& tr[data-has-detail] td": { borderBottom: "none" },
    "& tr[data-detail] td": { pt: "0", pb: "12px" },
    "& tbody tr:hover td": { bg: "transparent" },
  }),
  detail: css({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    pl: "38px",
    fontSize: "12.5px",
    lineHeight: 1.45,
    color: "ed.ink2",
    "& [data-kind]": { whiteSpace: "normal" },
  }),
  orgCell: css({ display: "flex", alignItems: "center", gap: "10px" }),
  avatarBlank: css({
    width: "28px",
    height: "28px",
    flexShrink: 0,
    border: "1px solid",
    borderColor: "ed.rule",
    bg: "ed.panel",
  }),
  orgName: css({
    fontFamily: "serif",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.2,
  }),
  progressBlock: css({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    py: "2px",
  }),
  copilotLine: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: "4px 12px",
  }),
  copilotLabel: css({
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink3",
  }),
  manual: css({ mt: "16px" }),
  manualForm: css({
    mt: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxWidth: "560px",
  }),
};
